import { useEffect, useMemo, useState } from 'react';

import DistributionSelect from 'entities/Charts/components/DistributionSelect';
import {
  BAR_CHART_HEIGHT,
  BAR_CHART_WIDTH,
  GENERAL_CONCEPTUAL_UNDERSTANDING,
  NO_EVIDENCE_OF_STRATEGY,
  SIGNIFICANT_ERRORS_IN_WORK,
  WORK_IS_COMPLETE_AND_CORRECT
} from 'entities/Charts/constants';
import {
  ChartFilterProps,
  useOverallResponseChartData
} from 'entities/Charts/sdk';

import BarChart from 'components/BarChart';

import { notifyError } from 'utils/notifications';

const OverallResponseDistributionBarChart = ({
  filters
}: {
  filters: ChartFilterProps;
}) => {
  const [distributionOption, setDistributionOption] = useState<string>('grade');

  const { data, isLoading } = useOverallResponseChartData({
    option: distributionOption,
    filters
  });

  const flatData = useMemo(() => {
    if (!data) return [];

    const { results } = data;

    return results.map((item) => {
      const sumOfTheResults = Object.values(item.group_results).reduce(
        (acc, value) => {
          const num = Number(value);
          return Number(acc) + (isNaN(num) ? 0 : num);
        },
        0
      ) as number;

      if (sumOfTheResults === 0) {
        return {
          group_field_type: item.group_field_type,
          group_field_value: item.group_field_value,
          [GENERAL_CONCEPTUAL_UNDERSTANDING]: 0,
          [WORK_IS_COMPLETE_AND_CORRECT]: 0,
          [NO_EVIDENCE_OF_STRATEGY]: 0,
          [SIGNIFICANT_ERRORS_IN_WORK]: 0
        };
      }

      const resultsInDistribution = Object.entries(item.group_results)
        .map(([key, value]) => ({
          [key]:
            value && sumOfTheResults
              ? Number(((Number(value) / sumOfTheResults) * 100).toFixed(2))
              : 0
        }))
        .reduce((acc, curr) => ({ ...acc, ...curr }), {});

      return {
        group_field_type: item.group_field_type,
        group_field_value: item.group_field_value,
        ...resultsInDistribution
      };
    });
  }, [data]);

  const messageForUser = useMemo(() => {
    if (data?.is_suppressed) {
      return 'You cannot view data for demographic subgroups smaller than 10 students!';
    }
    return '';
  }, [data]);

  useEffect(() => {
    if (
      messageForUser ===
      'You cannot view data for demographic subgroups smaller than 10 students!'
    ) {
      notifyError({ error: messageForUser });
    }
  }, [messageForUser]);

  return (
    <>
      {isLoading && (
        <BarChart
          loading
          series={[]}
          height={BAR_CHART_HEIGHT}
          width={BAR_CHART_WIDTH}
        />
      )}

      {!isLoading && (
        <BarChart
          dataset={flatData as readonly Record<string, any>[]}
          height={BAR_CHART_HEIGHT}
          width={BAR_CHART_WIDTH}
          xAxis={[{ dataKey: 'group_field_value' }]}
          yAxis={[
            {
              valueFormatter: (value: number) => `${value}%`
            }
          ]}
          localeText={{ noData: 'No data for selected filters' }}
          series={[
            {
              dataKey: GENERAL_CONCEPTUAL_UNDERSTANDING,
              label: GENERAL_CONCEPTUAL_UNDERSTANDING,
              stack: 'data',
              valueFormatter: (value) => `${value}%`
            },
            {
              dataKey: WORK_IS_COMPLETE_AND_CORRECT,
              label: WORK_IS_COMPLETE_AND_CORRECT,
              stack: 'data',
              valueFormatter: (value) => `${value}%`
            },
            {
              dataKey: NO_EVIDENCE_OF_STRATEGY,
              label: NO_EVIDENCE_OF_STRATEGY,
              stack: 'data',
              valueFormatter: (value) => `${value}%`
            },
            {
              dataKey: SIGNIFICANT_ERRORS_IN_WORK,
              label: SIGNIFICANT_ERRORS_IN_WORK,
              stack: 'data',
              valueFormatter: (value) => `${value}%`
            }
          ]}
          margin={{ top: 10, bottom: 30, left: 40, right: 10 }}
        />
      )}
      <DistributionSelect
        distributionOption={distributionOption}
        setDistributionOption={setDistributionOption}
      />
    </>
  );
};

export default OverallResponseDistributionBarChart;
